import { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { ALERT_TYPE, FLAGSHIP_SCREENVIEW_KPIS } from "app/constants";
import { FormattedMessage, useIntl } from "react-intl";
import IdentityFieldset from "app/pages/.shared/profile/IdentityFieldset";
import Email from "app/pages/.shared/profile/Email";
import PhoneIntl from "app/pages/.shared/profile/PhoneIntl";
import Separator from "app/pages/.shared/Separator";
import AddressFieldset from "app/pages/.shared/profile/AddressFieldset";
import { contactFields } from "app/pages/Booking/Payment/paymentSchema";
import get from "lodash/get";
import "./PaymentContact.scss";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import { HitType, useFlagship } from "@flagship.io/react-sdk";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { useLocation } from "react-router-dom";
import appGlobalsContext from "app/AppGlobalsContext";
import { initAdyen } from "app/utils/creditCardUtils";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const PaymentContact = ({
	errors = [],
	nameField = {},
	emailField = {},
	phoneField = {},
	addressField = {},
	onCountryChange,
	handleInputBlur,
	isLoading,
	shop,
	shouldFetchProfile,
}) => {
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen && !isLoading) {
			track("payment_view_contact");
		}
	}, [isElementOnScreen, isLoading]);

	const contactErrorList = [];
	const intl = useIntl();

	contactFields.forEach(path => {
		const error = get(errors, path);
		if (error) {
			contactErrorList.push(intl.formatMessage(error));
		}
	});

	// TODO déplacer l'envoi des ces hits dans PaymentContasiner une fois que le composant sera en functional component
	const { hit: fsHit } = useFlagship();
	const { pathname } = useLocation();
	const isSDP = pathname.includes("/sdp");
	useEffect(() => {
		fsHit.sendMultiple([
			{
				type: HitType.PAGE_VIEW,
				documentLocation: window.location.origin + window.location.pathname,
			},
			{
				type: HitType.SCREEN_VIEW,
				documentLocation: isSDP
					? FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_SDP_PAYMENT
					: FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_PP_PAYMENT,
			},
		]);
	}, [isSDP]);

	// TODO déplacer l'init de adyen dans PaymentContasiner une fois que le composant sera en functional component
	const { envVars } = useContext(appGlobalsContext);

	const adyenApiKey = envVars.ADYEN_API_KEY;

	useEffect(() => {
		initAdyen(adyenApiKey);
	}, [adyenApiKey]);

	return (
		<div className="payment-contact" ref={ref}>
			{isLoading ? (
				<LoadingPlaceholder
					message={<FormattedMessage id="payment.contact.loading.text" />}
				/>
			) : (
				<>
					{contactErrorList.length > 0 && (
						<AlertMessage
							alertType={ALERT_TYPE.ERROR}
							message={
								<FormattedMessage id="error.booking.payment.fields.required" />
							}
							errors={contactErrorList}
						/>
					)}

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.contact" />
					</Typography>

					<IdentityFieldset
						id="payment-contact"
						fields={nameField}
						isRequired={true}
						onBlur={handleInputBlur}
					/>

					<div className="payment__email-phone">
						{shouldFetchProfile ? (
							<Email email={emailField} disabled />
						) : (
							<Email email={emailField} onBlur={handleInputBlur} />
						)}
						<PhoneIntl
							id="phone"
							name="phone"
							field={phoneField}
							shop={shop}
							isRequired
							handleInputBlur={handleInputBlur}
							data-testid="phone-number-mobile-input"
						/>
					</div>

					<Separator design="middle" />

					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						isBold
						className="payment__subtitle"
						component="h3"
					>
						<FormattedMessage id="payment.contact.address" />
					</Typography>

					<AddressFieldset
						id="payment"
						shop={shop}
						fields={addressField}
						onCountryChange={onCountryChange}
						isRequired={true}
						onBlur={handleInputBlur}
					/>
				</>
			)}
		</div>
	);
};

PaymentContact.propTypes = {
	shop: PropTypes.string,
	shouldFetchProfile: PropTypes.bool,
	errors: PropTypes.array,
	nameField: PropTypes.object,
	emailField: PropTypes.object,
	phoneField: PropTypes.object,
	addressField: PropTypes.object,
	onCountryChange: PropTypes.func,
	handleInputBlur: PropTypes.func,
	isLoading: PropTypes.bool,
};

export default PaymentContact;
